<script lang="ts">
  import {
    Cobros, FarmaciaClinica, Cuentas,
    type ResumenCobros, type DocumentoFiscal, type Paciente, type CerrarCobroBody,
  } from '../lib/api';
  import { tenantCtx } from '../lib/tenant.svelte';
  import PacientePicker from './PacientePicker.svelte';
  import ModalTipoComprobante, { type TipoComprobante } from './ModalTipoComprobante.svelte';
  import ReciboImprimible from './ReciboImprimible.svelte';
  import BandejaPendientes from './BandejaPendientes.svelte';

  let pacienteId = $state('');
  let pacienteNombre = $state('');
  let resumen = $state<ResumenCobros | null>(null);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);
  let modalAbierto = $state(false);
  let procesando = $state(false);
  let formaPago = $state<'EFECTIVO' | 'TARJETA' | 'TRANSFERENCIA' | 'CUENTA'>('EFECTIVO');
  let ultimoDocumento = $state<DocumentoFiscal | null>(null);
  let lineasImpresion = $state<Array<{ descripcion: string; cantidad: number; precioUnitario: number; subtotal?: number }>>([]);
  let cargoDesc = $state('Consulta médica');
  let cargoPrecio = $state(350);
  let agregandoCargo = $state(false);

  const total = $derived(Number(resumen?.montoTotalPendiente ?? 0));
  const cuentaPrincipal = $derived(resumen?.cuentasAbiertas?.[0] ?? null);
  const ventasPendientes = $derived(resumen?.ventasPendientes ?? []);
  const postergados = $derived(resumen?.cobrosPostergados ?? []);
  const tenantNombre = $derived(tenantCtx.nombre ?? 'Clínica');

  async function cargarResumen() {
    if (!pacienteId) {
      resumen = null;
      return;
    }
    loading = true;
    error = null;
    try {
      resumen = await Cobros.resumen(pacienteId);
    } catch (e: any) {
      error = e?.message ?? String(e);
      resumen = null;
    } finally {
      loading = false;
    }
  }

  function onPickPatient(id: string, p?: Paciente) {
    pacienteId = id;
    pacienteNombre = p ? `${p.nombres} ${p.apellidos}`.trim() : '';
    ultimoDocumento = null;
    void cargarResumen();
  }

  function onBandejaSelect(id: string, nombre?: string) {
    pacienteId = id;
    pacienteNombre = nombre ?? '';
    ultimoDocumento = null;
    void cargarResumen();
  }

  function abrirModal() {
    if (!pacienteId) { error = 'Selecciona un paciente.'; return; }
    if (total <= 0) { error = 'No hay montos pendientes de cobro.'; return; }
    error = null;
    modalAbierto = true;
  }

  async function armarLineasImpresion(ventaIds: string[]) {
    const lineas: typeof lineasImpresion = [];
    for (const r of cuentaPrincipal?.renglones ?? []) {
      lineas.push({
        descripcion: r.descripcion,
        cantidad: Number(r.cantidad),
        precioUnitario: Number(r.precioUnitario),
        subtotal: Number(r.total ?? r.cantidad * r.precioUnitario),
      });
    }
    for (const vid of ventaIds) {
      try {
        const det = await FarmaciaClinica.venta(vid);
        for (const it of det.items ?? []) {
          lineas.push({
            descripcion: it.descripcion ?? 'Ítem farmacia',
            cantidad: Number(it.cantidad ?? 1),
            precioUnitario: Number(it.precioUnitario ?? 0),
            subtotal: Number(it.subtotal ?? 0),
          });
        }
      } catch { /* omit */ }
    }
    lineasImpresion = lineas;
  }

  async function agregarCargoServicio() {
    if (!pacienteId) { error = 'Selecciona un paciente.'; return; }
    if (!cargoDesc.trim()) { error = 'Indica la descripción del servicio.'; return; }
    agregandoCargo = true; error = null;
    try {
      let cuentaId = cuentaPrincipal?.id;
      if (!cuentaId) {
        const c = await Cuentas.abrir({ pacienteId });
        cuentaId = c.id;
      }
      await Cuentas.renglon(cuentaId!, {
        descripcion: cargoDesc.trim(),
        cantidad: 1,
        precioUnitario: Number(cargoPrecio) || 0,
      });
      info = `Cargo «${cargoDesc}» agregado a la cuenta.`;
      await cargarResumen();
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      agregandoCargo = false;
    }
  }

  async function confirmarCobro(tipo: TipoComprobante, fel?: { nit: string; nombre: string }) {
    procesando = true;
    error = null;
    info = null;
    try {
      const body: CerrarCobroBody = {
        pacienteId,
        tipoDocumento: tipo,
        formaPago,
        cuentaId: cuentaPrincipal?.id,
        ventaIds: ventasPendientes.map(v => v.id!).filter(Boolean),
        receptorNit: fel?.nit,
        receptorNombre: fel?.nombre,
      };
      const r = await Cobros.cerrar(body);
      modalAbierto = false;

      if (tipo === 'POSTERGAR') {
        info = `Cobro guardado para más tarde · Q${Number(r.total).toFixed(2)}`;
        ultimoDocumento = null;
      } else if (r.documento) {
        ultimoDocumento = r.documento;
        await armarLineasImpresion(body.ventaIds ?? []);
        const docLabel = r.documento.tipo === 'FACTURA' ? 'Factura' : 'Recibo';
        info = `${docLabel} ${r.documento.serie}-${r.documento.numero} emitido por Q${Number(r.documento.total).toFixed(2)}.`;
      }
      await cargarResumen();
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      procesando = false;
    }
  }
</script>

<section>
  <h2>Cobros · caja secretaría</h2>
  <p class="hint">
    Un solo punto de cobro: consulta ambulatoria + medicamentos despachados en farmacia.
    Al cerrar el pago elija recibo (habitual), factura FEL o postergar.
  </p>

  {#if error}<div class="err">{error}</div>{/if}
  {#if info}<div class="ok">{info}</div>{/if}

  <div class="layout">
    <BandejaPendientes onSelect={onBandejaSelect} />

    <div class="main">
      <div class="picker-row">
        <label style="flex:1;min-width:16rem;">Paciente
          <PacientePicker
            bind:value={pacienteId}
            onPick={onPickPatient}
            placeholder="Buscar paciente para cobrar…"
          />
        </label>
        <label>Forma de pago
          <select bind:value={formaPago}>
            <option value="EFECTIVO">Efectivo</option>
            <option value="TARJETA">Tarjeta</option>
            <option value="TRANSFERENCIA">Transferencia</option>
            <option value="CUENTA">Cuenta</option>
          </select>
        </label>
        <button type="button" onclick={cargarResumen} disabled={!pacienteId || loading}>
          {loading ? '…' : 'Actualizar'}
        </button>
      </div>

      {#if pacienteId && resumen}
        {#if postergados.length > 0}
          <div class="alert-postergado">
            {postergados.length} cobro(s) postergado(s) · Q{postergados.reduce((s, p) => s + Number(p.montoTotal), 0).toFixed(2)}
          </div>
        {/if}

        <div class="cols">
          <div class="col">
            <h3>Cuenta ambulatoria</h3>
            {#if pacienteId}
              <div class="cargo-rapido">
                <label>Descripción <input bind:value={cargoDesc} placeholder="Consulta médica" /></label>
                <label>Precio <input type="number" min="0" step="0.01" bind:value={cargoPrecio} style="width:100px" /></label>
                <button type="button" onclick={agregarCargoServicio} disabled={agregandoCargo}>
                  {agregandoCargo ? '…' : '+ Agregar cargo'}
                </button>
                <button type="button" class="ghost sm" onclick={() => { cargoDesc = 'Consulta médica'; cargoPrecio = 350; }}>Consulta Q350</button>
                <button type="button" class="ghost sm" onclick={() => { cargoDesc = 'Urgencia'; cargoPrecio = 500; }}>Urgencia Q500</button>
                <button type="button" class="ghost sm" onclick={() => { cargoDesc = 'Solo laboratorio'; cargoPrecio = 0; }}>Solo lab (manual)</button>
              </div>
            {/if}
            {#if cuentaPrincipal}
              <table>
                <thead><tr><th>Descripción</th><th>Cant.</th><th>P.U.</th><th>Total</th></tr></thead>
                <tbody>
                  {#each cuentaPrincipal.renglones ?? [] as r (r.id ?? r.descripcion)}
                    <tr>
                      <td>{r.descripcion}</td>
                      <td class="num">{r.cantidad}</td>
                      <td class="num">Q{Number(r.precioUnitario).toFixed(2)}</td>
                      <td class="num">Q{Number(r.total ?? 0).toFixed(2)}</td>
                    </tr>
                  {:else}
                    <tr><td colspan="4" class="empty">Sin renglones en cuenta.</td></tr>
                  {/each}
                </tbody>
                <tfoot>
                  <tr><td colspan="3"><strong>Subtotal cuenta</strong></td>
                    <td class="num"><strong>Q{Number(resumen.montoCuentasAbiertas).toFixed(2)}</strong></td></tr>
                </tfoot>
              </table>
            {:else}
              <p class="empty">Sin cuenta ambulatoria abierta.</p>
            {/if}
          </div>

          <div class="col">
            <h3>Farmacia pendiente de cobro</h3>
            {#if ventasPendientes.length > 0}
              <table>
                <thead><tr><th>Fecha</th><th>Notas</th><th>Total</th></tr></thead>
                <tbody>
                  {#each ventasPendientes as v (v.id)}
                    <tr>
                      <td>{v.fecha?.slice(0, 10) ?? '—'}</td>
                      <td>{v.notas ?? 'Despacho farmacia'}</td>
                      <td class="num">Q{Number(v.total ?? 0).toFixed(2)}</td>
                    </tr>
                  {/each}
                </tbody>
                <tfoot>
                  <tr><td colspan="2"><strong>Subtotal farmacia</strong></td>
                    <td class="num"><strong>Q{Number(resumen.montoVentasPendientes).toFixed(2)}</strong></td></tr>
                </tfoot>
              </table>
            {:else}
              <p class="empty">Sin despachos pendientes de cobro.</p>
            {/if}
          </div>
        </div>

        <div class="resumen">
          <span>Total pendiente: <strong>Q{total.toFixed(2)}</strong></span>
          {#if pacienteNombre}<span class="paciente">{pacienteNombre}</span>{/if}
        </div>

        <div class="acciones">
          <button type="button" class="primary" onclick={abrirModal} disabled={total <= 0}>
            Cobrar Q{total.toFixed(2)}
          </button>
        </div>
      {:else if pacienteId && loading}
        <p class="hint">Cargando cuenta…</p>
      {:else if !pacienteId}
        <p class="hint">Selecciona un paciente o elige uno de la bandeja de pendientes.</p>
      {/if}

      {#if ultimoDocumento}
        <ReciboImprimible
          documento={ultimoDocumento}
          pacienteNombre={pacienteNombre}
          lineas={lineasImpresion}
          onClose={() => { ultimoDocumento = null; }}
        />
      {/if}
    </div>
  </div>
</section>

<ModalTipoComprobante
  bind:open={modalAbierto}
  {total}
  procesando={procesando}
  onConfirm={confirmarCobro}
/>

<style>
  section { padding: 1rem; }
  h2 { margin: 0 0 0.2rem; font-size: 1.4rem; color: #111827; }
  h3 { margin: .2rem 0 .5rem; font-size: .95rem; color: #166534; }
  .hint { color: #6b7280; font-size: .9rem; max-width: 52rem; }
  .err { background: #fee2e2; color: #dc2626; padding: .6rem; border-radius: 8px; margin: .4rem 0; }
  .ok { background: #dcfce7; color: #166534; padding: .6rem; border-radius: 8px; margin: .4rem 0; }
  .layout { display: flex; gap: 1.25rem; flex-wrap: wrap; align-items: flex-start; margin-top: .75rem; }
  .main { flex: 1; min-width: 20rem; }
  .picker-row { display: flex; gap: .6rem; flex-wrap: wrap; align-items: end; margin-bottom: .75rem; }
  .picker-row label { display: flex; flex-direction: column; gap: .25rem; font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .cols { display: flex; gap: 1rem; flex-wrap: wrap; }
  .col { flex: 1; min-width: 16rem; }
  table { width: 100%; border-collapse: collapse; margin-top: .4rem; font-size: .88rem; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .5rem .5rem; background: #f0fdf4; }
  td { border-top: 1px solid #f3f4f6; padding: .4rem .5rem; text-align: left; color: #111827; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  tfoot td { border-top: 2px solid #e5e7eb; }
  .empty { color: #9ca3af; font-style: italic; font-size: .88rem; }
  .alert-postergado {
    background: #fef3c7; border-left: 4px solid #d97706;
    padding: .5rem .75rem; margin-bottom: .6rem; font-size: .88rem; color: #92400e;
  }
  .cargo-rapido { display: flex; flex-wrap: wrap; gap: .45rem; align-items: end; margin-bottom: .65rem; padding: .55rem; background: #f9fafb; border-radius: 8px; }
  .cargo-rapido label { display: flex; flex-direction: column; gap: .2rem; font-size: .72rem; font-weight: 600; color: #6b7280; }
  .cargo-rapido input { padding: .4rem .55rem; border: 1px solid #e5e7eb; border-radius: 6px; font: inherit; }
  button.ghost.sm { background: #fff; color: #166534; border: 1px solid #bbf7d0; box-shadow: none; padding: .35rem .6rem; font-size: .8rem; }
  .resumen {
    display: flex; gap: 1.5rem; flex-wrap: wrap; align-items: center;
    background: #f0fdf4; border-left: 4px solid #16a34a;
    padding: .65rem 1rem; margin-top: .85rem; border-radius: 8px;
  }
  .resumen strong { color: #166534; }
  .paciente { color: #6b7280; font-size: .88rem; }
  .acciones { margin-top: .65rem; }
  button.primary {
    background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border: 0;
    padding: .55rem 1.2rem; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600;
    box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s;
  }
  button.primary:hover:not(:disabled) { transform: translateY(-1px); }
  button.primary:disabled { opacity: .5; cursor: not-allowed; box-shadow: none; }
  select, button {
    padding: .45rem .55rem; border: 1px solid #e5e7eb; border-radius: 8px; font: inherit; background: #fff; color: #111827;
  }
  select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  button:not(.primary) { background: #fff; cursor: pointer; color: #16a34a; }
</style>
